import { timestampDate } from '@bufbuild/protobuf/wkt';
import { type Account } from '@qdrant/qdrant-cloud-public-api/account/v1';
import { type User } from '@qdrant/qdrant-cloud-public-api/iam/v1';
import { type StatsigUpdateDetails } from '@statsig/react-bindings';
import { CancelledError, queryOptions } from '@tanstack/react-query';
import { merge } from 'ts-deepmerge';
import { isStatsigClientReady } from '@/helpers/statsig';
import { createQueryKey, statsigUpdateUserQueryKey } from '../query-keys';
import { getStatsigClient } from '../statsig-client';

type StatsigUpdateUserQueryKey = [
  (typeof statsigUpdateUserQueryKey)[0],
  (typeof statsigUpdateUserQueryKey)[1],
  string,
  string | undefined,
  number | undefined,
  boolean,
];

export const statsigUpdateUserQueryOptions = queryOptions({
  queryKey: statsigUpdateUserQueryKey,
  queryFn: async ({ queryKey, signal }) => {
    const statsigClient = getStatsigClient();
    if (!statsigClient) {
      throw new Error('Statsig client is not available.');
    }
    // If the Statsig client is not fully initialized, we cannot proceed with context synchronization.
    if (!isStatsigClientReady(statsigClient)) {
      return null;
    }

    // Manual deferred instead of `Promise.withResolvers()`, which is unsupported on Safari < 17.4 / older iOS.
    let resolve!: (value: StatsigUpdateDetails) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<StatsigUpdateDetails>((res, rej) => {
      resolve = res;
      reject = rej;
    });

    // The Statsig SDK does not currently support passing an AbortSignal to its async methods,
    // but we want to be able to cancel the updateUserAsync call if the query is cancelled or times out.
    const onSignalAbort = () => {
      reject(
        signal.reason instanceof CancelledError
          ? new DOMException('The operation was aborted.', 'AbortError')
          : signal.reason,
      );
    };
    signal.addEventListener('abort', onSignalAbort, { once: true });
    try {
      const statsigUser = statsigClient.getContext().user ?? {};
      const [, , userId, accountId, userCreatedAt, isQdrantEmployee] = queryKey as StatsigUpdateUserQueryKey;
      const result = await statsigClient.updateUserAsync(
        // A user with no account has none to scope the gates to, only the `userID`. Every
        // account-derived key is assigned unconditionally so it clears together with the account:
        // `merge` overwrites with an explicit `undefined`, but keeps the previous value of a key the
        // new object omits — a conditional spread here would leave the old account id behind.
        merge(statsigUser, {
          customIDs: { account_id: accountId },
          custom: { account_id: accountId, userCreatedAt, is_qdrant_employee: isQdrantEmployee },
          userID: userId,
        }),
        // The module `@statsig/client-core/NetworkCore.js` has its own internal `signal`: `new AbortController().signal.abort('Timeout ...')`.
        // The `signal` is passed to `window.fetch` inside `updateUserAsync` to trigger timeouts,
        // but it does not support external abort signals for cancellation.
        { timeoutMs: 10_000 },
      );
      resolve(result);
    } catch (err) {
      if (typeof err === 'string' && err.toLowerCase().includes('timeout')) {
        reject(new DOMException(err, 'TimeoutError'));
      } else {
        reject(Error.isError(err) ? err : new Error(String(err)));
      }
    }
    signal.removeEventListener('abort', onSignalAbort);

    return promise;
  },
  staleTime: Infinity,
  retry: 1,
});

/**
 * The user context every feature gate is evaluated against. The account is optional — a user with no
 * accounts yet has none — and the user id alone still beats the anonymous user the client boots with.
 *
 * The key positions have to match `StatsigUpdateUserQueryKey`, which the query function reads them back through.
 */
export function createStatsigUpdateUserQuery({ user, account }: { user: User; account?: Account }) {
  const userCreatedAt = account?.createdAt ? Number(timestampDate(account.createdAt)) : undefined;
  const isQdrantEmployee = isQdrantEmployeeEmail(user.email);

  return {
    ...statsigUpdateUserQueryOptions,
    queryKey: createQueryKey(...statsigUpdateUserQueryKey, user.id, account?.id, userCreatedAt, isQdrantEmployee),
  };
}

// Statsig only gets the derived flag, never the email (PII). IAM sets the same `is_qdrant_employee`
// field on its Statsig user (qdrant-cloud-iam-service), so keep the field name and domain in sync with it.
const isQdrantEmployeeEmail = (email: string) => /@qdrant\.(com|io|tech)$/.test(email.trim().toLowerCase());
